/* eslint-disable jsx-a11y/href-no-hash */
const matomoTracker = {
  _growthbookInstance: null,
  _lastFilterState: null, // Cache para evitar disparos duplicados

  /**
   * Define a instância do GrowthBook para que o tracker possa acessar os experimentos ativos.
   * Deve ser chamado uma vez após a inicialização do GrowthBook.
   * @param {GrowthBook} gbInstance - A instância do GrowthBook.
   */
  setGrowthbookInstance: (gbInstance) => {
    matomoTracker._growthbookInstance = gbInstance;
  },

  /**
   * Adiciona as custom dimensions do GrowthBook ao _paq antes de cada evento.
   * Assume que as Custom Dimensions 1 e 2 estão configuradas no Matomo para:
   * CD1: Nome do Experimento (Escopo: Ação)
   * CD2: Variante do Experimento (Escopo: Ação)
   * Captura experimentos ativos, incluindo aqueles dentro de Feature Flags.
   * No seu caso, a flag "SP-Fluxo_de_compra" contém o experimento "Fluxo_de_compra_EXP".
   */
  _addGrowthbookExperimentContext: () => {
    if (window._paq && matomoTracker._growthbookInstance) {
      // 1. Verifica experimentos diretos
      const experiments = matomoTracker._growthbookInstance.getExperiments();
      experiments.forEach((exp) => {
        const result = matomoTracker._growthbookInstance.run(exp);
        if (result.inExperiment) {
          window._paq.push(['setCustomDimension', 1, exp.key]);
          window._paq.push(['setCustomDimension', 2, result.variationId]);
        }
      });

      // 2. Verifica experimentos vinculados a Feature Flags (como o seu SP-Fluxo_de_compra)
      // O GrowthBook armazena os resultados das flags avaliadas
      const features = matomoTracker._growthbookInstance.getFeatures();
      Object.keys(features).forEach((key) => {
        const result = matomoTracker._growthbookInstance.evalFeature(key);
        // Se a flag disparou um experimento (tem experimentResult)
        if (result.experimentResult && result.experimentResult.inExperiment) {
          const expName = result.experimentResult.name || result.experimentResult.key;
          window._paq.push(['setCustomDimension', 1, expName]); // Ex: Fluxo_de_compra_EXP
          window._paq.push(['setCustomDimension', 2, result.experimentResult.variationId]); // Ex: 0 ou 1
        }
      });
    }
  },

  /**
   * Limpa as custom dimensions do GrowthBook após cada evento.
   */
  _clearGrowthbookExperimentContext: () => {
    if (window._paq) {
      window._paq.push(['deleteCustomDimension', 1]);
      window._paq.push(['deleteCustomDimension', 2]);
    }
  },
  /**
   * Rastreia um evento customizado.
   */

  trackEvent: (category, action, name, value) => {
    if (window._paq) {
      matomoTracker._addGrowthbookExperimentContext();
      window._paq.push(['trackEvent', category, action, name, value]);
      matomoTracker._clearGrowthbookExperimentContext();
    }
  },

  /**
   * Rastreia visualização de página (SPA).
   */
  trackPageView: (customTitle) => {
    if (window._paq) {
      matomoTracker._addGrowthbookExperimentContext();
      window._paq.push(["setCustomUrl", window.location.pathname + window.location.search]);
      window._paq.push(['setDocumentTitle', customTitle || document.title]);
      window._paq.push(['trackPageView']);
      matomoTracker._clearGrowthbookExperimentContext();
    }
  },

  /**
   * Define o ID do usuário no Matomo.
   * @param {string} userId - O ID do usuário logado.
   */
  setUserId: (userId) => {
    if (window._paq) {
      window._paq.push(['setUserId', userId]);
    }
  },

  /**
   * Retorna o ID do visitante do Matomo (Promise).
   */
  getMatomoVisitorId: () => {
    return new Promise((resolve) => {
      if (window._paq) {
        window._paq.push([
          function () {
            resolve(this.getVisitorId());
          },
        ]);
      } else {
        resolve(null);
      }
    });
  },

  /**
   * 1. Visualização de Plano (Product View)
   * Chamado quando o usuário vê os detalhes de um plano ou a lista de resultados.
   * Push estruturado para MTM data layer com evento 'view_item'
   * @param {Object} plan - Objeto com dados do plano (ex: { sku: 'MTA60', name: 'MTA 60 Am. Latina', category: 'América do Sul', price: 284.99 })
   */
  setPlanView: (plan) => {
    if (window._paq) {
      matomoTracker._addGrowthbookExperimentContext();
      window._paq.push({
        'event': 'view_item',
        'ecommerce': {
          'items': [
            {
              'item_id': String(plan.sku) || null, // Product ID/SKU
              'item_name': plan.name, // Product Name
              'item_category': plan.category, // Product Category
              'item_price': parseFloat(plan.price) || null, // Product Price
            }
          ]
        }
      });
      matomoTracker._clearGrowthbookExperimentContext();
    }
  },

  /**
   * 2. Seleção de Plano (Add to Cart)
   * Chamado quando o usuário clica em "Selecionar Plano".
   * Push estruturado para MTM data layer com evento 'add_to_cart'
   * @param {Object} plan - Dados do plano selecionado.
   * @param {number} quantity - Número de segurados.
   */
  addPlanToCart: (plan, quantity = 1) => {
    if (window._paq) {
      matomoTracker._addGrowthbookExperimentContext();
      const cartTotal = parseFloat(plan.price) * parseInt(quantity, 10);
      window._paq.push({
        'event': 'add_to_cart',
        'ecommerce': {
          'items': [
            {
              'item_id': String(plan.sku) || null, // Product SKU/ID (required)
              'item_name': plan.name, // Product Name (optional)
              'item_category': plan.category, // Product Category (optional)
              'item_price': parseFloat(plan.price) || 0, // Product Price (recommended)
              'quantity': parseInt(quantity, 10) || 1 // Quantity (optional)
            }
          ],
          'total': cartTotal // Cart total
        }
      });
      matomoTracker._clearGrowthbookExperimentContext();
    }
  },

  /**
   * 3. Finalização de Compra (Ecommerce Order)
   * Chamado na página de confirmação de pagamento.
   * Push estruturado para MTM data layer com evento 'purchase'
   * @param {Object} order - Dados da reserva (id, total, etc).
   * @param {Array} items - Lista de planos/insureds incluídos.
   */
  trackOrder: (order, items) => {
    if (window._paq) {
      matomoTracker._addGrowthbookExperimentContext();
      
      // Formata os itens no padrão ecommerce.items
      const formattedItems = items.map(item => ({
        'item_id': String(item.sku) || null,
        'item_name': item.name,
        'item_category': item.category || null,
        'item_price': parseFloat(item.price) || 0,
        'quantity': parseInt(item.quantity, 10) || 1
      }));
      
      window._paq.push({
        'event': 'purchase',
        'ecommerce': {
          'purchase': {
            'id': String(order.id), // Order ID (required)
            'revenue': parseFloat(order.grandTotal) || 0, // Grand Total (required)
            'orderSubTotal': parseFloat(order.subTotal) || 0, // Sub Total (optional)
            'tax': parseFloat(order.tax) || 0, // Tax (optional)
            'shipping': parseFloat(order.shipping) || 0, // Shipping (optional)
            'discount': parseFloat(order.discount) || 0 // Discount (optional)
          },
          'items': formattedItems
        }
      });
      
      matomoTracker._clearGrowthbookExperimentContext();
    }
  },

  /**
   * 4. Eventos de Funil Específicos (Lead Generation)
   * Rastreia interações importantes que não são venda direta.
   */
  trackSearch: (destination, startDate, daysInAdvance) => {
    matomoTracker._addGrowthbookExperimentContext();
    matomoTracker.trackEvent('Funnel', 'Search', `${destination} | ${startDate} | ${daysInAdvance}advp`);
    matomoTracker._clearGrowthbookExperimentContext();
  },

  trackComparison: (planNames) => {
    matomoTracker._addGrowthbookExperimentContext();
    matomoTracker.trackEvent('Funnel', 'ComparePlans', planNames.join(' vs '));
    matomoTracker._clearGrowthbookExperimentContext();
  },

  /**
   * Rastreia a aplicação de filtros na tela de resultados.
   * Evita disparos duplicados verificando se o estado mudou.
   * @param {Object} filters - Objeto contendo os filtros aplicados (ex: { providers: ['Affinity'], coverage: 100000, benefits: ['Gestante'] })
   * @param {number} resultsCount - Quantidade de planos encontrados após o filtro.
   */
  trackFilters: (filters, resultsCount) => {
    if (window._paq) {
      // Cria um hash do estado atual para comparação
      const currentState = JSON.stringify({ filters, resultsCount });
      
      // Só dispara se o estado mudou
      if (matomoTracker._lastFilterState === currentState) {
        return; // Evita disparo duplicado
      }
      
      // Atualiza o cache do último estado
      matomoTracker._lastFilterState = currentState;
      
      matomoTracker._addGrowthbookExperimentContext();

      // Transforma o objeto de filtros em uma string legível para o Matomo
      const filterLabels = [];
      if (filters.providers && filters.providers.length > 0) filterLabels.push(`Seguradoras: ${filters.providers.join(',')}`);
      if (filters.coverage) filterLabels.push(`Cobertura: ${filters.coverage}`);
      if (filters.benefits && filters.benefits.length > 0) filterLabels.push(`Benefícios: ${filters.benefits.join(',')}`);

      const label = filterLabels.join(' | ') || 'Nenhum filtro';

      // Rastreia como um evento de interação
      window._paq.push(["trackEvent", "Search", "ApplyFilter", label, resultsCount]);

      matomoTracker._clearGrowthbookExperimentContext();
    }
  },
};

export default matomoTracker;


// WEBPACK FOOTER //
// ./src/utils/matomo.js